import { format, addDays } from 'date-fns';
import { useEffect, useState } from 'react';

import { getCurrentBlockOutDates, hasCurrentBlockOutDates, renderCustomizeLookPath } from 'utils/utils';
import { BlockoutDate, Item } from '../types';
import { ItemPrice } from '@root/src/partials/products/Price';
import { PromotionalPricing, usePromotionalPricing } from 'contexts/AppContext';
import { useCustomerEventList } from '@root/src/components/CustomerEventList';
import { useRouter } from 'next/router';
import BundleRadioPrices, { RentOrBuyOptionsType } from './BundleRadioPrices';
import { htoFloatingButtonClicked } from 'utils/metrics';
import { usePurchaseOptionSelection } from 'contexts/AppContext';

interface CustomizeButtonProps {
  product: Item;
  radioButtonSelection: RentOrBuyOptionsType;
  builderQuery: string;
  isBlockedOut: boolean;
  isComingSoonBlockedOut: boolean;
}

const useCustomizeButtonLink = () => {
  const [link, setLink] = useState(`${process.env.NEXT_PUBLIC_APP_URL}/customize`);

  useEffect(() => {
    setLink(renderCustomizeLookPath());
  }, []);

  return link;
};

const CustomizeButton = ({
  product,
  radioButtonSelection,
  builderQuery,
  isBlockedOut,
  isComingSoonBlockedOut,
}: CustomizeButtonProps) => {
  const [isCustomizeBtnDisabled, setIsCustomizedBtnDisabled] = useState(false);
  const [isNormalBlock, setIsNormalBlock] = useState(false);

  const getCustomizeBtnText = (product: Item) => {
    if (product.is_retail) {
      return 'Customize Purchase';
    }

    const productHasRetailBundle = (product: Item) =>
      product.retail_bundle?.displayable && product.retail_bundle?.is_active;

    const isRetail = productHasRetailBundle(product) ? radioButtonSelection === 'Buy' : product.type === 'Purchase';

    return `Customize ${isRetail ? 'Purchase' : 'Rental'}`;
  };

  const getBuilderQuery = () => {
    const isBundle = product.category!.toLowerCase() === 'preconfigured';

    const param = builderQuery.split('=')[0];

    if (!isBundle) {
      return `${param}=${product.sku}`;
    }

    if (radioButtonSelection === 'Rent') {
      return `${param}=${product.id}`;
    }

    //A preconfigured bundle can not have a retail_bundle but have product.id
    return `${param}=${product.retail_bundle?.id! ?? product.id}`;
  };

  const getFirstAvailableDate = (blockoutDates: BlockoutDate[]) => {
    const currentDates = getCurrentBlockOutDates(blockoutDates);
    const date = currentDates.filter((date) => date.availability === 'coming-soon').shift();
    const endDate = date?.endDate ?? date?.end_date;

    if (!endDate) {
      throw new Error('Called getFirstAvailableDate() without any qualifying blockout dates');
    }

    const availableDate = addDays(
      new Date(endDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/New_York',
      }),
      2
    );

    return format(availableDate, 'MMM. do, yyyy');
  };

  useEffect(() => {
    setIsCustomizedBtnDisabled(isBlockedOut || isComingSoonBlockedOut);
    setIsNormalBlock(isBlockedOut && !isComingSoonBlockedOut);
  }, [isBlockedOut, isComingSoonBlockedOut]);

  const customizeBtnLink = useCustomizeButtonLink();

  return (
    <div className="space-y-8 pt-8 sm:mt-32">
      <a
        href={!isCustomizeBtnDisabled ? `${customizeBtnLink}${getBuilderQuery()}` : '#'}
        role="button"
        tabIndex={0}
        className={`tracker-cta-product_page-hero_customize-20190208-091145 btn-info btn relative z-10 w-full ${
          isCustomizeBtnDisabled ? 'disabled' : ''
        }`}
        onClick={(ev) => isCustomizeBtnDisabled && ev.preventDefault()}
        aria-disabled={isCustomizeBtnDisabled}
        referrerPolicy="unsafe-url"
      >
        {getCustomizeBtnText(product)}
      </a>

      {isNormalBlock && (
        <p className="text-sm">
          Customize disabled for limited items.{' '}
          <a
            href={`${process.env.NEXT_PUBLIC_APP_URL}/signup/email`}
            className="tracker-cta-product_page-hero_sign_up-200623-093050"
          >
            Create an event
          </a>{' '}
          to see if this item is available for your date.
        </p>
      )}

      {isComingSoonBlockedOut && (
        <div className="text-sm mt-8">
          Available {getFirstAvailableDate(product.blockoutDates ?? product.blockout_dates ?? [])}, but you can keep
          planning! Build your look,{' '}
          <a
            href={`${process.env.NEXT_PUBLIC_APP_URL}/signup/email`}
            className="tracker-cta-product_page-hero_sign_up-200623-093050 inline-block"
          >
            create an event
          </a>
          , & order swatches today.
        </div>
      )}
    </div>
  );
};

/**
 * Returns the discounted price that's applicable to the given item, or `null` if
 * there isn't one
 */
const getDiscountedPrice = (item: Item, promotionalPricing: PromotionalPricing | null) => {
  if (!promotionalPricing) {
    return null;
  }

  if (promotionalPricing.itemType === 'product') {
    return promotionalPricing.value;
  }

  const { rentalValue, retailValue } = promotionalPricing;
  const isRetail = item.is_retail ?? item.isRetail ?? false;

  if (isRetail && retailValue) {
    return retailValue;
  } else if (!isRetail && rentalValue) {
    return rentalValue;
  }

  return null;
};

type CustomizeBoxProps = {
  isBlockedOut: boolean;
  isComingSoonBlockedOut: boolean;
  builderQueryString: string;
  item: Item;
  showHtoBtn?: boolean;
  htoUrl?: string;
  isBundle?: boolean;
  showOnMobile?: boolean;
};

const CustomizeBox = (props: CustomizeBoxProps) => {
  const { item, builderQueryString, showHtoBtn, htoUrl, isBundle, isComingSoonBlockedOut, showOnMobile } = props;

  const promotionalPricing = usePromotionalPricing(item);
  const { primary: primaryEvent } = useCustomerEventList();
  const router = useRouter();

  const isSelectionBlockedOut = (val: RentOrBuyOptionsType) => {
    if (!router.isReady) {
      return false;
    }

    const url = router.isReady ? router.asPath : '';

    if (url.includes('collection') && item.retail_bundle && val === 'Buy') {
      const blockoutDates = item.retail_bundle?.blockout_dates ?? [];
      const nonHTOBlockouts = blockoutDates.filter((x) => x.availability !== 'hto');

      return hasCurrentBlockOutDates(nonHTOBlockouts, primaryEvent?.startDate);
    }

    return props.isBlockedOut || props.isComingSoonBlockedOut;
  };

  const canCustomizeProduct = item.displayable && (item.is_active || item.isActive);
  const isRentalItem = item.type?.toLowerCase() !== 'purchase' && !(item.is_retail ?? item.isRetail ?? false);
  const discountedPrice = getDiscountedPrice(item, promotionalPricing);

  const purchaseOptionsSelection = usePurchaseOptionSelection();

  return (
    <div className="mt-8">
      {canCustomizeProduct ? (
        <>
          {isBundle ? (
            <div className={`${!showOnMobile ? 'hidden sm:block' : 'block'}`}>
              <BundleRadioPrices bundle={item} />
            </div>
          ) : (
            <>
              <div className={`${isBundle ? 'hidden sm:flex' : 'flex'} items-end justify-between`}>
                <p className="mr-8 leading-3">{isRentalItem ? 'Rent' : 'Buy'}</p>

                <div className="h-2 flex-1 bg-leader bg-bottom bg-repeat-x" />

                <p className={`text-h4 ml-8 leading-3 ${discountedPrice ? 'text-gray' : 'text-gray-dark'}`}>
                  <ItemPrice price={item.cost!} discountedPrice={discountedPrice} />
                  {discountedPrice && (
                    <span className="font-condensed-light normal-case text-red-light sm-only:hidden">
                      {' '}
                      with code <span className="promo-code">{promotionalPricing?.promoCode}</span>
                    </span>
                  )}
                </p>
              </div>

              {discountedPrice && (
                <div className="!mt-16 hidden justify-end pl-8 font-condensed-light text-red-light sm-only:flex">
                  With code <span className="promo-code mx-4">{promotionalPricing?.promoCode}</span> at checkout
                </div>
              )}
            </>
          )}

          <CustomizeButton
            product={item}
            radioButtonSelection={purchaseOptionsSelection.selection}
            builderQuery={builderQueryString}
            isBlockedOut={isSelectionBlockedOut(purchaseOptionsSelection.selection)}
            isComingSoonBlockedOut={isComingSoonBlockedOut}
          />

          {showHtoBtn && isBundle && (
            <a
              href={htoUrl}
              role="button"
              tabIndex={1}
              className={`tracker-cta-product_page-hero_free-hto-20250408-122803 btn-info btn relative z-10 mt-16 w-full sm:hidden`}
              onClick={() =>
                htoFloatingButtonClicked({ itemName: item.displayName ?? item.display_name ?? '', itemId: item.id })
              }
            >
              Free Home Try-On
            </a>
          )}
        </>
      ) : (
        <p className={`${isBundle ? 'hidden sm:block' : 'block'}`}>Product No Longer Available</p>
      )}
    </div>
  );
};

export default CustomizeBox;
